<script setup lang="ts">
const props = defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  search: { type: Boolean, default: false },
  breadcrumbs: {
    type: Array as PropType<{ title: string; link: string }[]> | null,
    default: null
  },
  back: { type: Boolean, default: false },
  cancel: { type: Boolean, default: false },
  reverse: { type: Boolean, default: false },
  disableHome: { type: Boolean, default: false }
})

const { data: user } = useAuth()

const router = useRouter()

const goBack = () => {
  if (props.back || props.cancel) router.back()
}

defineEmits(['clickSearch'])

const homeLink = computed(() => getHomePage(user.value))
</script>

<template>
  <div class="page-header">
    <div v-if="back" class="page-header-back">
      <div class="back-btn clickable" @click="goBack"><CommonIconArrow /> <span>Back</span></div>
    </div>

    <div v-if="breadcrumbs" class="page-header-breadcrumbs breadcrumbs">
      <NuxtLink v-if="!disableHome" :to="homeLink"><CommonIconSidebarHome /> </NuxtLink>
      <b v-if="!disableHome"> > </b>
      <div v-for="(link, i) in breadcrumbs" :key="`breadcrumb-link-${link.title}`">
        <NuxtLink :to="link.link"> {{ link.title }} </NuxtLink>
        <b v-if="i !== breadcrumbs.length - 1">></b>
      </div>
    </div>

    <div class="page-header-main">
      <div v-if="title" :class="`page-header-title ${reverse ? 'reverse' : ''}`">
        <div class="page-header-title-text">
          <h1>{{ title }}</h1>

          <div v-if="search" class="page-header-title-search" @click="$emit('clickSearch')">
            <CommonIconSearch class="clickable" />
          </div>
        </div>
        <span v-if="subtitle !== ''" class="page-header-title-subtitle">{{ subtitle }}</span>
      </div>

      <div class="page-header-content">
        <div v-if="cancel" class="cancel-btn clickable" @click="goBack">Cancel</div>
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-header {
  padding-top: 35px;

  &-back {
    margin-bottom: 20px;

    .back-btn {
      display: inline-flex;
      align-items: center;
      gap: 7px;

      svg {
        transform: scaleX(-1);
        fill: var(--textM2);
        height: 10px;
        width: 10px;
      }

      span {
        font-size: 0.83rem;
        color: var(--textM2);
        font-weight: 500;
      }
    }
  }

  &-breadcrumbs {
    margin-bottom: 25px;

    a {
      color: var(--textM2);
      font-size: 0.8rem;
      font-weight: 400;
    }

    @media (hover: hover) {
      a:hover {
        color: var(--purple);
      }
    }

    b {
      padding: 0 12px;
    }

    svg {
      $homeSize: 12px;
      height: $homeSize;
      width: $homeSize;
      fill: var(--textM2);
    }
  }

  &-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h1 {
    font-size: 1.5rem;
    height: 35px;
    display: flex;
    align-items: center;
  }

  &-title {
    $searchIcon: 27px;

    &-text {
      display: grid;
      grid-template-columns: auto $searchIcon;
      gap: 10px;
      align-items: center;
    }

    &-search {
      height: $searchIcon;
      width: $searchIcon;
      border-radius: 7px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--borderM2);

      svg {
        $iconSize: 16px;
        height: $iconSize;
        width: $iconSize;
        fill: vaR(--black);
        transition: fill 0.3s ease-out;
      }
    }

    &-subtitle {
      display: block;
      margin-top: 5px;
      font-size: 0.93rem;
      color: vaR(--textM2);
    }
  }

  .reverse {
    display: flex;
    flex-direction: column-reverse;

    .page-header-title-subtitle {
      margin-top: 0;
      margin-bottom: 12px;
    }
  }

  .cancel-btn {
    font-size: 0.87rem;
    color: var(--textM2);
    font-weight: 500;
  }

  &-content {
    display: flex;
    gap: 7px;
  }
}

@media (max-width: 600px) {
  .page-header {
    h1 {
      font-size: 1.3rem;
      height: auto;
    }

    .breadcrumbs,
    &-breadcrumbs {
      display: flex;
      flex-wrap: wrap;
      white-space: nowrap;
      line-height: 1.5rem;
      overflow-x: hidden;
      width: 100%;
    }
  }
}

@media (hover: hover) {
  .page-header {
    .back-btn:hover {
      span {
        color: var(--purple);
      }

      svg {
        fill: var(--purple);
      }
    }

    .cancel-btn:hover {
      color: var(--purple);
    }
  }
}
</style>
